/**
 * 천장(pity) 카운터 — interval 기반 보장 카운터. 도메인 무관(등급/아이템/통화 모름) 순수 로직.
 *
 * 호출자가 매 pull마다 `advance()`를 호출하고, `true`(= interval 도달 → "보장 pull")일 때
 * 자신의 도메인 보장(예: 최소 등급 상향, 특정 아이템 확정)을 적용한다. 그룹(10연)이든 단발이든
 * 같은 인스턴스의 `advance()`를 누적 호출하면 한 카운터로 합산된다(단발·묶음 공용 천장).
 *
 * 저장은 `toState()`/생성자 `initial`로 직렬화 — 외부 SaveData에 `counter`만 보존하면 복원된다.
 */
export interface PityState {
  /** 마지막 보장 이후 누적된 pull 횟수(0..interval-1). interval 도달 순간 0으로 리셋된다. */
  readonly counter: number;
}

export interface PityOptions {
  /** 보장 주기 — 이 횟수째 pull이 보장 pull이 된다(예: 10이면 10·20·30…회째). 1 미만은 1로 클램프. */
  readonly interval: number;
}

/**
 * interval 도달마다 1회 보장을 발생시키는 누적 카운터.
 * `advance()`는 부수효과(카운터 증가/리셋)가 있고, `willGuarantee()`는 순수 미리보기다.
 */
export class PityCounter {
  private _counter: number;
  private readonly _interval: number;

  constructor(options: PityOptions, initial?: PityState) {
    this._interval = Math.max(1, Math.floor(options.interval));
    // 저장 손상/오버플로 방어 — [0, interval) 범위로 정규화(interval 이상이면 다음 advance가 즉시 보장).
    this._counter = Math.min(this._interval - 1, Math.max(0, Math.floor(initial?.counter ?? 0)));
  }

  get counter(): number {
    return this._counter;
  }

  get interval(): number {
    return this._interval;
  }

  /** 다음 `advance()`가 보장 pull인지 미리보기 — 부수효과 없음(카운터 불변). */
  willGuarantee(): boolean {
    return this._counter + 1 >= this._interval;
  }

  /** 1회 pull 진행 — 카운터 +1. interval 도달 시 카운터를 0으로 리셋하고 `true`(보장) 반환. */
  advance(): boolean {
    this._counter += 1;
    if (this._counter >= this._interval) {
      this._counter = 0;
      return true;
    }
    return false;
  }

  toState(): PityState {
    return { counter: this._counter };
  }
}
